import { useErrorHandlingQuery } from '@backoffice-frontend/data-fetching'
import { ArrowLeftIcon } from '@moia-dev/pace-icons'
import {
  Heading,
  IconButton,
  Input,
  LoadingIndicator,
  Text,
} from '@moia-dev/pace-web'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { vtrLocationMatchesCity } from '../utils'
import { useRouteBuilderVtrTicketsQuery } from './TripEditor.hook'

type VtrTicketSelectorProps = {
  metropolisName: string | undefined
  onSelect: (ticketKey: string) => void
}

export const VtrTicketSelector = ({
  metropolisName,
  onSelect,
}: VtrTicketSelectorProps) => {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')

  const { data, loading } = useErrorHandlingQuery(
    useRouteBuilderVtrTicketsQuery,
    { variables: {} },
  )

  const tickets = useMemo(() => {
    // First scope to the selected metropolis, then apply the search filter.
    const inCity = (data?.vtrTickets ?? []).filter(t =>
      vtrLocationMatchesCity(t.location, metropolisName),
    )
    if (!search.trim()) return inCity
    const q = search.toLowerCase()
    return inCity.filter(
      t =>
        t.key.toLowerCase().includes(q) ||
        t.summary.toLowerCase().includes(q) ||
        t.location.toLowerCase().includes(q),
    )
  }, [data?.vtrTickets, metropolisName, search])

  return (
    <div css={{ display: 'flex', height: '100%' }}>
      <div
        css={theme => ({
          width: 480,
          flexShrink: 0,
          borderRight: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        })}
      >
        <div
          css={theme => ({
            display: 'flex',
            alignItems: 'center',
            gap: theme.space.Space2,
            padding: theme.space.Space4,
            paddingBottom: theme.space.Space3,
            borderBottom: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
          })}
        >
          <IconButton
            aria-label="Back to saved trips"
            size="compact"
            variant="plain"
            onClick={() => navigate('../saved-trips')}
            icon={<ArrowLeftIcon />}
          />
          <Heading size="md" css={{ flex: 1 }}>
            Create from VTR
          </Heading>
        </div>

        <div
          css={theme => ({
            padding: theme.space.Space4,
            paddingBottom: theme.space.Space2,
          })}
        >
          <Input
            size="compact"
            placeholder="Search tickets..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {metropolisName != null && (
            <Text
              size="sm"
              color="reduced"
              css={theme => ({ marginTop: theme.space.Space2 })}
            >
              Showing tickets for: {metropolisName}
            </Text>
          )}
        </div>

        <div
          css={theme => ({
            flex: 1,
            overflow: 'auto',
            padding: theme.space.Space4,
            paddingTop: theme.space.Space2,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.space.Space2,
          })}
        >
          {loading && (
            <div
              css={{ display: 'flex', justifyContent: 'center', padding: 32 }}
            >
              <LoadingIndicator size={24} />
            </div>
          )}
          {!loading && tickets.length === 0 && (
            <Text
              size="sm"
              color="reduced"
              css={{ textAlign: 'center', padding: 32 }}
            >
              No VTR tickets found
            </Text>
          )}
          {tickets.map(ticket => (
            <div
              key={ticket.key}
              role="button"
              tabIndex={0}
              css={theme => ({
                padding: theme.space.Space3,
                cursor: 'pointer',
                border: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
                borderRadius: 8,
                backgroundColor: theme.semantic.ColorBackgroundDefault,
                '&:hover': {
                  backgroundColor: theme.semantic.ColorBackgroundAlternative,
                },
              })}
              onClick={() => onSelect(ticket.key)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onSelect(ticket.key)
                }
              }}
            >
              <div
                css={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Text size="md" weight="bold">
                  {ticket.key}
                </Text>
                <Text
                  size="sm"
                  css={theme => ({
                    color:
                      ticket.status === 'Open'
                        ? theme.semantic.ColorSuccessDefault
                        : theme.semantic.ColorAccessoryDefault,
                  })}
                >
                  {ticket.status}
                </Text>
              </div>
              <Text
                size="sm"
                css={theme => ({ marginTop: theme.space.Space1 })}
              >
                {ticket.summary}
              </Text>
              <div
                css={theme => ({
                  display: 'flex',
                  gap: theme.space.Space3,
                  marginTop: theme.space.Space2,
                })}
              >
                <Text size="sm" color="reduced">
                  {ticket.location}
                </Text>
                <Text size="sm" color="reduced">
                  {ticket.missionType}
                </Text>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        css={theme => ({
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.semantic.ColorBackgroundDimmedReduced,
        })}
      >
        <Text size="lg" color="reduced">
          Select a VTR ticket to begin
        </Text>
      </div>
    </div>
  )
}
